/**
 * Web 行为漏斗看板（ADM-MTR-S016）共享契约：GET /api/v1/metrics/web-funnels 的查询参数与响应形态。
 *
 * 口径唯一见 dev-docs `03-数据字典/06-Web行为漏斗.md`，端点见 AD §19：
 * - 单个 WEB App × 单个分析；起点日 S 窗口受 90 天保留期约束（to ≤ 昨天、from ≥ 今天 − 89 天由路由按当前时刻校验）
 * - 每个计数单位一段（segments），段间不给比例；第 2 级起只在成熟起点（S + 6 ≤ last_complete_day）中计数
 * - 未就绪返回占位对象 PENDING_INSTRUMENTATION / PENDING_MATURITY（AD §0.2），不返回 0 或 mock
 */

import { z } from 'zod';
import {
  PAYWALL_CONVERSION_MAX_DAYS,
  metricsCalendarDay,
  metricsRepeatedListParam,
} from './metrics-paywall-conversion';

/** 分析闭集（06 §3.1–§3.7 + §4） */
export const WEB_FUNNEL_ANALYSES = [
  'new_visitor',
  'ad_link',
  'reading_depth',
  'locked_chapter',
  'price_payment',
  'a2hs_guide',
  'activity_retention',
  'settlement_check',
] as const;
export type WebFunnelAnalysis = (typeof WEB_FUNNEL_ANALYSES)[number];

/** 分组维度全集；各分析可用子集见 WEB_FUNNEL_DIMENSIONS_BY_ANALYSIS */
export const WEB_FUNNEL_DIMENSIONS = [
  'date',
  'source',
  'ad_link',
  'book',
  'chapter',
  'language',
  'country',
  'scenario',
  'product',
  'trigger',
  'environment',
  'device_platform',
] as const;
export type WebFunnelDimension = (typeof WEB_FUNNEL_DIMENSIONS)[number];

export const WEB_FUNNEL_DIMENSIONS_BY_ANALYSIS: Record<
  WebFunnelAnalysis,
  readonly WebFunnelDimension[]
> = {
  new_visitor: ['date', 'source', 'ad_link'],
  ad_link: ['date', 'ad_link', 'book', 'language', 'country'],
  reading_depth: ['date', 'book', 'source', 'language', 'country'],
  locked_chapter: ['date', 'book', 'chapter', 'language', 'country'],
  price_payment: ['date', 'scenario', 'product', 'device_platform'],
  a2hs_guide: ['date', 'trigger', 'environment', 'device_platform'],
  activity_retention: ['date', 'source', 'environment', 'device_platform'],
  settlement_check: ['date'],
};

/** 筛选参数 → 可用分析（AD §19.1） */
export const WEB_FUNNEL_FILTERS_BY_ANALYSIS = {
  languages: ['ad_link', 'reading_depth', 'locked_chapter'],
  countries: ['ad_link', 'reading_depth', 'locked_chapter'],
  sources: ['new_visitor', 'reading_depth', 'activity_retention'],
  novel_ids: ['ad_link', 'reading_depth', 'locked_chapter'],
  ad_link_ids: ['ad_link'],
  chapter_index: ['ad_link'],
  max_chapter_index: ['reading_depth'],
} as const satisfies Record<string, readonly WebFunnelAnalysis[]>;

/** 各分析恒含、不可取消的分组维度 */
export const WEB_FUNNEL_REQUIRED_DIMENSIONS: Partial<
  Record<WebFunnelAnalysis, WebFunnelDimension>
> = { a2hs_guide: 'environment', settlement_check: 'date' };

/** 以重复查询参数传递的多值参数名（路由按此把同名参数收成数组） */
export const WEB_FUNNEL_LIST_PARAMS = [
  'languages',
  'countries',
  'sources',
  'novel_ids',
  'ad_link_ids',
] as const;

export const WEB_FUNNEL_SOURCES = ['NON_ORGANIC', 'ORGANIC'] as const;
export type WebFunnelSource = (typeof WEB_FUNNEL_SOURCES)[number];

/** 单次查询跨度上限（与付费 / 激活转化看板同源：受 90 天保留期约束） */
export const WEB_FUNNEL_MAX_DAYS = PAYWALL_CONVERSION_MAX_DAYS;
/** 分组行返回上限；超出时该段 `truncated=true` */
export const WEB_FUNNEL_MAX_ROWS = 5000;
/** 按书阅读深度每个分组行带一条曲线：分组行 × 曲线章数不超过该值，超出按行截断（AD §19.3） */
export const WEB_FUNNEL_MAX_CURVE_POINTS = 200_000;

/** 分组行上限：按书阅读深度另受曲线点数约束 */
export function webFunnelMaxRows(query: {
  analysis: WebFunnelAnalysis;
  max_chapter_index?: number;
}) {
  if (query.analysis !== 'reading_depth') return WEB_FUNNEL_MAX_ROWS;
  const chapters = query.max_chapter_index ?? WEB_FUNNEL_DEFAULT_MAX_CHAPTER_INDEX;
  return Math.min(WEB_FUNNEL_MAX_ROWS, Math.floor(WEB_FUNNEL_MAX_CURVE_POINTS / chapters));
}
/** 06 §2.5 观察窗：起点日 S 起 7 个 UTC 日 */
export const WEB_FUNNEL_OBSERVATION_DAYS = 7;
export const WEB_FUNNEL_DEFAULT_MAX_CHAPTER_INDEX = 50;
export const WEB_FUNNEL_MAX_CHAPTER_INDEX_LIMIT = 200;

const uuidList = metricsRepeatedListParam(z.string().uuid()).transform((ids) =>
  ids.map((id) => id.toLowerCase()),
);

export const webFunnelQuerySchema = z
  .object({
    app_id: z
      .string()
      .uuid()
      .transform((id) => id.toLowerCase())
      .describe('单个 WEB App 的 UUID（非 WEB App → VALIDATION_FAILED）'),
    analysis: z
      .enum(WEB_FUNNEL_ANALYSES)
      .describe('分析：06 §3.1–§3.7 七个有序分析或 §4 到账核对，一次一个'),
    from: metricsCalendarDay.describe('起点日 S 闭区间起点（UTC 日，不早于今天 − 89 天）'),
    to: metricsCalendarDay.describe('起点日 S 闭区间终点（UTC 日，不晚于昨天）'),
    group_by: z
      .string()
      .optional()
      .transform((raw) => [
        ...new Set(
          (raw ?? '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
        ),
      ])
      .pipe(z.array(z.enum(WEB_FUNNEL_DIMENSIONS)).max(WEB_FUNNEL_DIMENSIONS.length))
      .describe('逗号分隔的分组维度；闭集按分析不同（AD §19.1），缺省不分组'),
    languages: metricsRepeatedListParam(z.string().max(35)).describe(
      '第 1 级事件 language_hint，重复参数多值',
    ),
    countries: metricsRepeatedListParam(
      z.string().regex(/^[A-Z]{2}$/, '国家须为 ISO 3166-1 alpha-2'),
    ).describe('第 1 级事件 country_hint（ISO 3166-1 alpha-2），重复参数多值'),
    sources: metricsRepeatedListParam(z.enum(WEB_FUNNEL_SOURCES)).describe(
      '首访来源：NON_ORGANIC 投放 / ORGANIC 自然',
    ),
    novel_ids: uuidList.describe('限书（novel UUID），重复参数多值'),
    ad_link_ids: uuidList.describe('限投放链接（ad_link UUID），仅 ad_link'),
    chapter_index: z.coerce
      .number()
      .int()
      .min(1)
      .optional()
      .describe('ad_link 用户段第 3 级的 k；缺省取该书收费起点'),
    max_chapter_index: z.coerce
      .number()
      .int()
      .min(1)
      .max(WEB_FUNNEL_MAX_CHAPTER_INDEX_LIMIT)
      .optional()
      .describe('reading_depth 曲线返回到第几章，缺省 50'),
  })
  // 拒绝未声明参数：拼错的筛选静默丢弃会返回看似合理的未筛选数据
  .strict()
  .superRefine((query, ctx) => {
    const span =
      (Date.parse(`${query.to}T00:00:00Z`) - Date.parse(`${query.from}T00:00:00Z`)) / 86_400_000;
    if (span < 0) ctx.addIssue({ code: 'custom', path: ['to'], message: 'to 不得早于 from' });
    else if (span + 1 > WEB_FUNNEL_MAX_DAYS)
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: `单次查询最多 ${WEB_FUNNEL_MAX_DAYS} 天`,
      });
    const allowedDimensions = WEB_FUNNEL_DIMENSIONS_BY_ANALYSIS[query.analysis];
    for (const dimension of query.group_by) {
      if (!allowedDimensions.includes(dimension))
        ctx.addIssue({
          code: 'custom',
          path: ['group_by'],
          message: `${query.analysis} 不支持按 ${dimension} 分组`,
        });
    }
    for (const [param, analyses] of Object.entries(WEB_FUNNEL_FILTERS_BY_ANALYSIS)) {
      const value = query[param as keyof typeof WEB_FUNNEL_FILTERS_BY_ANALYSIS];
      const present = Array.isArray(value) ? value.length > 0 : value !== undefined;
      if (present && !(analyses as readonly string[]).includes(query.analysis))
        ctx.addIssue({
          code: 'custom',
          path: [param],
          message: `${query.analysis} 不支持参数 ${param}`,
        });
    }
  })
  // 恒含的维度（AD §19.1）：主屏引导恒按 environment（06 §3.6 第 3、4 级按第 1 级打开环境返回），
  // 到账核对恒按日
  .transform((query) => {
    const required = WEB_FUNNEL_REQUIRED_DIMENSIONS[query.analysis];
    return required && !query.group_by.includes(required)
      ? { ...query, group_by: [...query.group_by, required] }
      : query;
  });
export type WebFunnelQuery = z.infer<typeof webFunnelQuerySchema>;

export type WebFunnelPendingStatus = 'PENDING_INSTRUMENTATION' | 'PENDING_MATURITY';
/** 占位对象（AD §0.2）：非 error、非 0、非 null */
export type WebFunnelPending = { status: WebFunnelPendingStatus };
export type WebFunnelCount = number | WebFunnelPending;

export type WebFunnelUnit =
  | 'OPEN'
  | 'SESSION'
  | 'USER'
  | 'USER_PRODUCT'
  | 'USER_CHAPTER'
  | 'USER_BOOK'
  | 'DAY';

export type WebFunnelStage = {
  key: string;
  /** 只在成熟、适用的起点中计数；占位对象时 eligible 为 null */
  count: WebFunnelCount;
  eligible: number | null;
};

/** 付费后拆分（06 §3.4）：到达与结果之间是否有 web_purchase_settled，按其 scenario 分 */
export type WebFunnelPaidSplit = { paid_reader: number; paid_recharge: number; unpaid: number };
export type WebFunnelLockedOutcomes = {
  record: WebFunnelPaidSplit;
  free_pass: WebFunnelPaidSplit;
  unknown_path: number;
  left: number;
};

export type WebFunnelMetrics = {
  /** 第 1 级：全部起点（含未成熟） */
  starts: WebFunnelCount;
  /** 成熟起点数；按日留存段不适用时为 null */
  mature_starts: number | null;
  stages: WebFunnelStage[];
  /** ad_link 打开段：失败按 resolution_result 计数 */
  failures?: Record<string, number> | WebFunnelPending;
  /** reading_depth：到达第 k 章的 (用户, 书) 数，只在成熟起点中计数 */
  curve?: { chapter_index: number; count: number }[] | WebFunnelPending;
  paywall_start_chapter_index?: number | null;
  /** locked_chapter：全部起点按到达类型 */
  arrivals?: { locked: number; auto_unlock: number } | WebFunnelPending;
  outcomes?: WebFunnelLockedOutcomes | WebFunnelPending;
};

export type WebFunnelDimensionValues = Partial<Record<WebFunnelDimension, string | null>>;
export type WebFunnelRow = WebFunnelMetrics & { dims: WebFunnelDimensionValues };

export type WebFunnelActiveDay = { date: string; active_users: WebFunnelCount };
export type WebFunnelSettlementDay = {
  date: string;
  observed_settled: WebFunnelCount;
  settled: number;
  gap: WebFunnelCount;
  gap_rate_pct: number | null | WebFunnelPending;
};

export type WebFunnelSegment = {
  key: string;
  unit: WebFunnelUnit;
  summary?: WebFunnelMetrics;
  rows?: WebFunnelRow[];
  truncated?: boolean;
  days?: WebFunnelActiveDay[] | WebFunnelSettlementDay[];
};

export type WebFunnelOptions = {
  languages: string[];
  countries: string[];
  ad_links: { id: string }[];
  books: { id: string; title: string }[];
  chapters: { id: string; novel_id: string; chapter_index: number }[];
  products: { id: string }[];
};

export type WebFunnelData = {
  app: { id: string; name: string };
  analysis: WebFunnelAnalysis;
  window: { from: string; to: string; timezone: 'UTC' };
  last_complete_day: string;
  coverage: {
    coverage_start: string | null;
    observable_start: string | null;
    session_ready_start: string | null;
  };
  group_by: WebFunnelDimension[];
  segments: WebFunnelSegment[];
  options: WebFunnelOptions;
  generated_at: string;
};

export function isWebFunnelPending(value: unknown): value is WebFunnelPending {
  return typeof value === 'object' && value !== null && 'status' in value;
}

// ── 响应 schema（OpenAPI 用；z.ZodType<WebFunnelData> 标注保证与上方类型逐字段一致）──

const webFunnelPendingSchema = z
  .object({ status: z.enum(['PENDING_INSTRUMENTATION', 'PENDING_MATURITY']) })
  .describe('占位对象（AD §0.2）');
const webFunnelCountSchema = z.union([z.number().int(), webFunnelPendingSchema]);
const webFunnelPaidSplitSchema = z.object({
  paid_reader: z.number().int(),
  paid_recharge: z.number().int(),
  unpaid: z.number().int(),
});
const webFunnelMetricsSchema = z.object({
  starts: webFunnelCountSchema.describe('第 1 级：全部起点（含未成熟）'),
  mature_starts: z.number().int().nullable(),
  stages: z.array(
    z.object({
      key: z.string(),
      count: webFunnelCountSchema,
      eligible: z.number().int().nullable().describe('成熟且适用的起点数；占位时为 null'),
    }),
  ),
  failures: z.union([z.record(z.string(), z.number().int()), webFunnelPendingSchema]).optional(),
  curve: z
    .union([
      z.array(z.object({ chapter_index: z.number().int(), count: z.number().int() })),
      webFunnelPendingSchema,
    ])
    .optional(),
  paywall_start_chapter_index: z.number().int().nullable().optional(),
  arrivals: z
    .union([
      z.object({ locked: z.number().int(), auto_unlock: z.number().int() }),
      webFunnelPendingSchema,
    ])
    .optional(),
  outcomes: z
    .union([
      z.object({
        record: webFunnelPaidSplitSchema,
        free_pass: webFunnelPaidSplitSchema,
        unknown_path: z.number().int(),
        left: z.number().int(),
      }),
      webFunnelPendingSchema,
    ])
    .optional(),
});
const webFunnelDimensionValuesSchema = z.partialRecord(
  z.enum(WEB_FUNNEL_DIMENSIONS),
  z.string().nullable(),
);

export const webFunnelDataSchema: z.ZodType<WebFunnelData> = z.object({
  app: z.object({ id: z.string(), name: z.string() }),
  analysis: z.enum(WEB_FUNNEL_ANALYSES),
  // 以下字段说明见 AD §19.2 / §19.3
  window: z.object({ from: z.string(), to: z.string(), timezone: z.literal('UTC') }),
  last_complete_day: z.string().describe('最晚已走完的 UTC 日（昨天）'),
  coverage: z
    .object({
      coverage_start: z.string().nullable(),
      observable_start: z.string().nullable(),
      session_ready_start: z.string().nullable(),
    })
    .describe('就绪起点；null 表示未就绪（AD §19.2）'),
  group_by: z.array(z.enum(WEB_FUNNEL_DIMENSIONS)),
  segments: z.array(
    z.object({
      key: z.string().describe('段键：每个计数单位一段，段间不给比例'),
      unit: z.enum(['OPEN', 'SESSION', 'USER', 'USER_PRODUCT', 'USER_CHAPTER', 'USER_BOOK', 'DAY']),
      summary: webFunnelMetricsSchema.optional(),
      rows: z
        .array(webFunnelMetricsSchema.extend({ dims: webFunnelDimensionValuesSchema }))
        .optional(),
      truncated: z.boolean().optional(),
      days: z
        .union([
          z.array(z.object({ date: z.string(), active_users: webFunnelCountSchema })),
          z.array(
            z.object({
              date: z.string(),
              observed_settled: webFunnelCountSchema,
              settled: z.number().int(),
              gap: webFunnelCountSchema,
              gap_rate_pct: z.union([z.number(), z.null(), webFunnelPendingSchema]),
            }),
          ),
        ])
        .optional(),
    }),
  ),
  options: z.object({
    languages: z.array(z.string()),
    countries: z.array(z.string()),
    ad_links: z.array(z.object({ id: z.string() })),
    books: z.array(z.object({ id: z.string(), title: z.string() })),
    chapters: z.array(
      z.object({ id: z.string(), novel_id: z.string(), chapter_index: z.number().int() }),
    ),
    products: z.array(z.object({ id: z.string() })),
  }),
  generated_at: z.string(),
});
